/**
 * Wizard — multi-step linear workflow primitive.
 *
 * Phase 8 Prompt 3 — per audit deliverable 04D Pattern P1 + Section E
 * reference implementation.
 *
 * Used by: Campaign Manager (4-step Audience → Content → Schedule → Review),
 * Ad-hoc Composer (same with Send Now default), Audience Manager
 * "Build" flow, Funnel starter selection, etc.
 *
 * Features:
 *   - Numbered step indicator (click-to-jump to completed steps)
 *   - Per-step validation; Continue disabled until step is valid
 *   - Auto-save every step transition (integrate via onStepChange)
 *   - Resume-from-draft via `defaultStep` prop
 *   - Mobile responsive (collapses to "Step 2 of 4" caption)
 *
 * Usage:
 *   <Wizard
 *     steps={[
 *       { id: 'audience', title: 'Audience', render: (form, setForm) => <StepAudience .../>, validate: (form) => form.audienceId ? null : 'Pick an audience' },
 *       { id: 'content', title: 'Content', render: ..., validate: ... },
 *       { id: 'schedule', title: 'Schedule', render: ..., validate: ... },
 *       { id: 'review', title: 'Review', render: ..., validate: () => null },
 *     ]}
 *     onComplete={async (form) => apiService.post('/campaigns', form)}
 *     autoSaveStatus="saved"
 *     lastSaved={lastSavedDate}
 *   />
 */

import React, { useState, useMemo, useCallback } from "react";
import {
  Box,
  Typography,
  LinearProgress,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { ActionButton } from "./ActionButton";
import { AutoSaveStatus, secondsAgo } from "../../hooks/useAutoSave";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface WizardStep<TFormState> {
  /** Stable step id. */
  id: string;
  /** Display title (e.g. "Audience"). */
  title: string;
  /** Optional sub-title shown below the indicator. */
  description?: string;
  /** Renders the step content. Receives current form state + setter. */
  render: (
    form: TFormState,
    setForm: (next: TFormState) => void,
  ) => React.ReactNode;
  /**
   * Validates the current step. Return null if valid; return an error
   * message string to block "Continue".
   */
  validate: (form: TFormState) => string | null;
  /** Optional help-content tooltip id rendered next to the title. */
  helpTipId?: string;
}

export interface WizardProps<TFormState> {
  steps: WizardStep<TFormState>[];
  /**
   * Initial form state. Seeds the UNCONTROLLED mode only — it is read once, so
   * later changes to the object are ignored. Pass `value` instead when the
   * parent needs to be the source of truth.
   */
  initialState: TFormState;
  /**
   * Controlled mode. When `value` is provided the wizard renders THIS object
   * and never keeps a private copy, so parent-side effects (recipient
   * resolution, name hydration, validation, the complete-button label) all see
   * the same state the operator is editing. Pair with `onChange`.
   *
   * Leave undefined for the original uncontrolled behaviour.
   */
  value?: TFormState;
  /** Called with the next form state on every edit (both modes). */
  onChange?: (next: TFormState) => void;
  /** Called on final step "Complete". */
  onComplete: (form: TFormState) => void | Promise<void>;
  /** Called when the wizard is fully cancelled. */
  onCancel?: () => void;
  /** Called whenever a step changes (use for analytics or auto-save). */
  onStepChange?: (
    fromStepId: string | null,
    toStepId: string,
    form: TFormState,
  ) => void;
  /** Optional Save Draft handler. If set, a Save Draft button is rendered. */
  onSaveDraft?: (form: TFormState) => Promise<void>;
  /** Optional auto-save status indicator (from useAutoSave). */
  autoSaveStatus?: AutoSaveStatus;
  lastSaved?: Date | null;
  /** Step to start on (1-indexed). Default 1. Use for resume-from-draft. */
  defaultStep?: number;
  /** Label for the final "Complete" button. Default: "Complete". */
  completeLabel?: string;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function Wizard<TFormState>({
  steps,
  initialState,
  value,
  onChange,
  onComplete,
  onCancel,
  onStepChange,
  onSaveDraft,
  autoSaveStatus,
  lastSaved,
  defaultStep = 1,
  completeLabel = "Complete",
}: WizardProps<TFormState>): JSX.Element {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [stepIndex, setStepIndex] = useState(
    Math.min(Math.max(defaultStep - 1, 0), steps.length - 1),
  );
  const isControlled = value !== undefined;
  const [internalForm, setInternalForm] = useState<TFormState>(initialState);
  const form = isControlled ? (value as TFormState) : internalForm;
  const setForm = useCallback(
    (next: TFormState) => {
      if (!isControlled) setInternalForm(next);
      onChange?.(next);
    },
    [isControlled, onChange],
  );
  const [highestCompletedIndex, setHighestCompletedIndex] = useState(
    Math.max(stepIndex - 1, -1),
  );
  const [submitting, setSubmitting] = useState(false);

  const currentStep = steps[stepIndex];
  const validationError = useMemo(
    () => currentStep?.validate(form),
    [currentStep, form],
  );
  const isLastStep = stepIndex === steps.length - 1;

  const goToStep = useCallback(
    (idx: number) => {
      if (idx < 0 || idx >= steps.length) return;
      if (onStepChange) {
        onStepChange(currentStep?.id ?? null, steps[idx].id, form);
      }
      setStepIndex(idx);
    },
    [currentStep, form, onStepChange, steps],
  );

  const handleContinue = useCallback(async () => {
    if (validationError) return;
    if (isLastStep) {
      setSubmitting(true);
      try {
        await onComplete(form);
      } finally {
        setSubmitting(false);
      }
    } else {
      setHighestCompletedIndex((prev) => Math.max(prev, stepIndex));
      goToStep(stepIndex + 1);
    }
  }, [validationError, isLastStep, form, onComplete, goToStep, stepIndex]);

  const handleBack = useCallback(() => {
    if (stepIndex === 0) return;
    goToStep(stepIndex - 1);
  }, [stepIndex, goToStep]);

  const handleStepClick = useCallback(
    (idx: number) => {
      // Operator can click back to any completed step
      if (idx <= highestCompletedIndex || idx === stepIndex) {
        goToStep(idx);
      }
    },
    [highestCompletedIndex, stepIndex, goToStep],
  );

  const handleSaveDraft = async () => {
    if (!onSaveDraft) return;
    await onSaveDraft(form);
  };

  return (
    <Box sx={{ width: "100%" }}>
      {/* Step indicator */}
      <StepIndicator
        steps={steps}
        currentIndex={stepIndex}
        highestCompletedIndex={highestCompletedIndex}
        onStepClick={handleStepClick}
        isMobile={isMobile}
      />

      {/* Step content */}
      <Box
        sx={{
          minHeight: 300,
          py: 3,
        }}
      >
        <Box sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
          <Typography
            variant="overline"
            sx={{
              color: "text.secondary",
              fontFamily: '"Montserrat", sans-serif',
              fontWeight: 700,
              letterSpacing: "1.5px",
            }}
          >
            Step {stepIndex + 1} of {steps.length}
          </Typography>
        </Box>

        <Typography
          variant="h5"
          sx={{
            fontFamily: '"Playfair Display", serif',
            fontWeight: 600,
            color: "text.primary",
            mb: 0.5,
          }}
        >
          {currentStep.title}
        </Typography>

        {currentStep.description && (
          <Typography
            variant="body2"
            sx={{ color: "text.secondary", mb: 2 }}
          >
            {currentStep.description}
          </Typography>
        )}

        <Box sx={{ mt: 2 }}>{currentStep.render(form, setForm)}</Box>
      </Box>

      {/* Footer */}
      <Box
        sx={{
          borderTop: "1px solid",
          borderColor: "divider",
          pt: 2,
          mt: 2,
          display: "flex",
          flexDirection: { xs: "column-reverse", sm: "row" },
          alignItems: { xs: "stretch", sm: "center" },
          justifyContent: "space-between",
          gap: 1.5,
        }}
      >
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <ActionButton
            verb="cancel"
            labelOverride={stepIndex === 0 ? "Cancel" : "← Back"}
            onClick={stepIndex === 0 ? (onCancel ?? (() => {})) : handleBack}
            showIcon={false}
            variantOverride="ghost"
          />

          {/* Auto-save indicator */}
          {autoSaveStatus && (
            <AutoSaveIndicator status={autoSaveStatus} lastSaved={lastSaved} />
          )}
        </Box>

        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          {validationError && !isMobile && (
            <Typography
              variant="caption"
              sx={{
                color: "#7D1935",
                fontFamily: '"Montserrat", sans-serif',
                fontWeight: 600,
              }}
            >
              {validationError}
            </Typography>
          )}

          {onSaveDraft && !isLastStep && (
            <ActionButton
              verb="save_draft"
              onClick={handleSaveDraft}
              variantOverride="secondary"
            />
          )}

          <ActionButton
            verb={isLastStep ? "publish" : "edit"}
            labelOverride={
              isLastStep ? completeLabel : `Continue → ${steps[stepIndex + 1]?.title ?? ""}`
            }
            onClick={handleContinue}
            disabled={Boolean(validationError) || submitting}
            loading={submitting}
            showIcon={false}
            variantOverride="primary"
            size="large"
          />
        </Box>
      </Box>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Step Indicator
// ---------------------------------------------------------------------------

function StepIndicator<T>({
  steps,
  currentIndex,
  highestCompletedIndex,
  onStepClick,
  isMobile,
}: {
  steps: WizardStep<T>[];
  currentIndex: number;
  highestCompletedIndex: number;
  onStepClick: (idx: number) => void;
  isMobile: boolean;
}) {
  if (isMobile) {
    // Mobile: just a progress bar + "Step 2 of 4: Content"
    return (
      <Box>
        <LinearProgress
          variant="determinate"
          value={((currentIndex + 1) / steps.length) * 100}
          sx={{
            height: 4,
            borderRadius: 2,
            "& .MuiLinearProgress-bar": { backgroundColor: "#D4AC0D" },
          }}
        />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        gap: 0,
        borderBottom: "2px solid",
        borderColor: "divider",
      }}
    >
      {steps.map((step, idx) => {
        const isCurrent = idx === currentIndex;
        const isCompleted = idx <= highestCompletedIndex && idx < currentIndex;
        const isClickable = idx <= highestCompletedIndex || idx === currentIndex;
        const color = isCurrent
          ? "#D4AC0D"
          : isCompleted
            ? "#146152"
            : "#5D6D7E";

        return (
          <Box
            key={step.id}
            onClick={() => isClickable && onStepClick(idx)}
            sx={{
              flex: 1,
              cursor: isClickable ? "pointer" : "default",
              textAlign: "center",
              py: 1,
              px: 0.5,
              fontFamily: '"Montserrat", sans-serif',
              fontSize: "0.85rem",
              fontWeight: isCurrent ? 700 : 600,
              color,
              borderBottom: `3px solid ${
                isCurrent ? "#D4AC0D" : isCompleted ? "#146152" : "transparent"
              }`,
              marginBottom: "-2px",
              transition: "color 200ms, border 200ms",
              "&:hover": isClickable
                ? { backgroundColor: "rgba(0,0,0,0.02)" }
                : undefined,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 0.6,
            }}
            aria-current={isCurrent ? "step" : undefined}
            aria-disabled={!isClickable}
          >
            <Box
              component="span"
              sx={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 22,
                height: 22,
                borderRadius: "50%",
                bgcolor: color,
                color: "#fff",
                fontSize: "0.7rem",
                fontWeight: 700,
                flexShrink: 0,
              }}
            >
              {isCompleted ? (
                <CheckCircleIcon sx={{ fontSize: 16 }} />
              ) : (
                idx + 1
              )}
            </Box>
            {step.title}
          </Box>
        );
      })}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Auto-save indicator
// ---------------------------------------------------------------------------

const AutoSaveIndicator: React.FC<{
  status: AutoSaveStatus;
  lastSaved?: Date | null;
}> = ({ status, lastSaved }) => {
  const styles = {
    saving: { color: "#5D6D7E", text: "💾 Saving…" },
    saved: {
      color: "#146152",
      text: `✓ Saved ${secondsAgo(lastSaved ?? null)}`,
    },
    error: { color: "#7D1935", text: "⚠ Save failed" },
    idle: { color: "#5D6D7E", text: "" },
  }[status];

  if (!styles.text) return null;

  return (
    <Typography
      component="span"
      sx={{
        color: styles.color,
        fontFamily: '"Montserrat", sans-serif',
        fontSize: "0.8rem",
        fontWeight: 600,
        ml: 1,
      }}
    >
      {styles.text}
    </Typography>
  );
};

export default Wizard;
